import { ToastManagerRepository } from "@core/domain/repositories/toast-manager.repository";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Undefinable } from "@core/domain/types/undefinable.type";
import { LoadLayoutStore } from "@core/presentation/components/feedback/load-layout/load-layout.store";
import type { Farm, FarmUpdate } from "@farm/domain/models/farm.model";
import { FarmUpdateUseCase } from "@farm/domain/usecases/farm-update.usecase";
import type { FarmModalFormValuesValidated } from "@farm/presentation/components/modal/farm/farm-modal-form.view";
import type { UserSessionRepository } from "@iot-platform/authentication";
import { authenticationTypes } from "@iot-platform/authentication";
import { BaseViewModel } from "@iot-platform/core";
import type { UserResume } from "@iot-platform/user";
import {
    UserGetAllResumeUseCase,
    UserGetByEmailUseCase,
    UserRoleEnum,
    Users,
} from "@iot-platform/user";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class FarmUpdateModalViewModel extends BaseViewModel {
    @observable
    initialLoading = true;

    @observable
    usersResume: Users = Users.empty();

    @observable
    farm: Undefinable<Readonly<Farm>> = undefined;

    constructor(
        @inject(FarmUpdateUseCase)
        private readonly farmUpdateUseCase: FarmUpdateUseCase,
        @inject(UserGetAllResumeUseCase)
        private readonly userGetAllResumeUseCase: UserGetAllResumeUseCase,
        @inject(UserGetByEmailUseCase)
        private readonly userGetByEmailUseCase: UserGetByEmailUseCase,
        @inject(authenticationTypes.repositories.UserSessionRepository)
        private readonly userSessionRepository: UserSessionRepository,
    ) {
        super();
        makeObservable(this);
    }

    override didMount(): void {
        this.initViewData();

        // Only administrators may browse the full user list; other roles add
        // users by email lookup instead, so the listing is never fetched.
        if (this.isSuperUser) {
            void this.fetchUsersResume();
        }
    }

    @action
    initViewData(): void {
        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    async updateFarm(
        values: FarmModalFormValuesValidated,
    ): Promise<Nullable<Farm>> {
        LoadLayoutStore.start();

        const farmData: FarmUpdate = {
            id: Number(values.id.value),
            name: values.name,
            enabledSatellites: values.enabledSatellites,
            userIds: values.userIds,
        };

        const farmUpdated = await this.farmUpdateUseCase.execute(
            Number(values.id.value),
            farmData,
        );

        if (farmUpdated) {
            ToastManagerRepository.success();
        }

        LoadLayoutStore.finish();

        return farmUpdated;
    }

    @action
    setFarm(farm: Readonly<Farm>): void {
        runInAction(() => {
            this.farm = farm;
        });
    }

    async findUserByEmail(email: string): Promise<Nullable<UserResume>> {
        return this.userGetByEmailUseCase.execute(email);
    }

    private get isSuperUser(): boolean {
        return (
            this.userSessionRepository.userSync?.rol === UserRoleEnum.SuperUser
        );
    }

    private async fetchUsersResume(): Promise<void> {
        const users = await this.userGetAllResumeUseCase.execute();
        runInAction(() => {
            this.usersResume = users;
        });
    }
}
